import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { PageLink } from "@/constants/groupConfig";

interface Props {
  page: PageLink;
  isFavorite: boolean;
  onOpen: (page: PageLink) => void;
  onToggleFavorite: (pageUrl: string) => void;
  /** Shown while the reader is arranging their home page. */
  onMove?: (pageUrl: string, direction: "up" | "down") => void;
  /** Set while a tile is being dragged into another folder. */
  onDragStart?: (pageUrl: string) => void;
}

/**
 * One page, as a card.
 *
 * The star sits on the card rather than behind a menu: starring is the one thing a reader does to
 * a page besides opening it, and it has to be reachable without leaving the folder they are in.
 */
export function PageTile({
  page,
  isFavorite,
  onOpen,
  onToggleFavorite,
  onMove,
  onDragStart,
}: Props) {
  const { t } = useTranslation();
  const favoriteLabel = isFavorite
    ? t("home.unfavorite", "Aus den Favoriten entfernen")
    : t("home.favorite", "Zu den Favoriten");

  return (
    <Card
      draggable={!!onDragStart}
      onDragStart={event => {
        // Firefox cancels a drag whose start carries no data, which left moving a page into
        // another folder doing nothing at all there.
        event.dataTransfer.setData("text/plain", page.addInUrl);
        onDragStart?.(page.addInUrl);
      }}
      sx={{
        position: "relative",
        borderRadius: 3,
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { boxShadow: 6, transform: "scale(1.03)" },
      }}
    >
      <CardActionArea onClick={() => onOpen(page)} sx={{ p: 2, height: "100%" }}>
        <Stack alignItems="center" spacing={1}>
          <Box sx={{ color: "text.secondary", display: "flex" }}>{page.icon}</Box>
          <Typography variant="subtitle1" align="center">
            {page.name}
          </Typography>
        </Stack>
      </CardActionArea>

      <Stack direction="row" sx={{ position: "absolute", top: 4, right: 4 }}>
        {onMove ? (
          <>
            <IconButton
              size="small"
              aria-label={t("home.moveUp", "Nach vorne")}
              onClick={() => onMove(page.addInUrl, "up")}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t("home.moveDown", "Nach hinten")}
              onClick={() => onMove(page.addInUrl, "down")}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </>
        ) : null}

        <Tooltip title={favoriteLabel}>
          <IconButton
            size="small"
            aria-label={favoriteLabel}
            onClick={() => onToggleFavorite(page.addInUrl)}
          >
            {isFavorite ? (
              <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
}
